class ServicePlanError extends Error {
    constructor(message, type = 'GENERIC') {
        super(message);
        this.type = type;
    }
}

class ServicePlan extends HTMLElement {
    constructor() {
        super();
        this.isLineItem = this.dataset.prefix !== 'pdp';
        this.debouncedHandleSubPlanChange = this.debounce(this.handleSubPlanChange.bind(this), 300);
        this.init();
    }

    connectedCallback() {
        this.setupEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    init() {
        if (this.isLineItem) {
            const rowWrapperEl = this.closest('tr') ? this.closest('tr') : this.closest('li');
            const parentQtyEl = this.closest('tr')
                ? this.closest('tr').previousSibling
                : this.closest('li');

            this.parentIndex = parentQtyEl.dataset.index;
            this.parentQty = parentQtyEl.querySelector('.quantity__input').value;
            this.loadingSpinner = this.querySelector('.loading__spinner');
            this.loadingSpinner.classList.add('hidden');
            this.properties = this.querySelector('[type="application/json"]');
        }

        this.cacheElements();
    }

    cacheElements() {
        this.addPlanCheckbox = this.querySelector('input[name="add-service-plan"]');
        this.planTypeRadios = this.querySelectorAll('input[name*="service-plan-type"]');
        this.subPlanRadios = this.querySelectorAll('input[name*="_service_plan"]');
    }

    setupEventListeners() {
        this.addPlanCheckbox.addEventListener('change', this.handleAddPlanChange.bind(this));
        this.planTypeRadios.forEach((radio) => {
            radio.addEventListener('change', this.handlePlanTypeChange.bind(this));
        });
        this.subPlanRadios.forEach((radio) => {
            radio.addEventListener('change', this.debouncedHandleSubPlanChange);
        });
    }

    removeEventListeners() {
        this.addPlanCheckbox.removeEventListener('change', this.handleAddPlanChange);
        this.planTypeRadios.forEach((radio) => {
            radio.removeEventListener('change', this.handlePlanTypeChange);
        });
        this.subPlanRadios.forEach((radio) => {
            radio.removeEventListener('change', this.debouncedHandleSubPlanChange);
        });
    }

    debounce(fn, delay) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    async fetchCartUpdate(data) {
        try {
            const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return await response.json();
        } catch (error) {
            throw new ServicePlanError(error.message, 'CART_FETCH');
        }
    }

    async clearAllPlans() {
        const ajaxCart =
            document.querySelector('cart-items') || document.querySelector('ajax-cart');
        if (!ajaxCart) return;

        ajaxCart.loading = true;
        this.loadingSpinner.classList.remove('hidden');

        try {
            await this.fetchCartUpdate({
                line: this.parentIndex,
                properties: {
                    _service_plan: ''
                }
            });
            ajaxCart.onCartUpdate();
        } catch (error) {
            console.error('Error updating service plan:', error);
            this.showErrorToUser('Failed to clear service plans. Please try again.');
        } finally {
            this.loadingSpinner.classList.add('hidden');
            ajaxCart.loading = false;
        }
    }

    handleAddPlanChange() {
        if (this.addPlanCheckbox.checked) {
            this.planTypeRadios[0].checked = true;
            this.handlePlanTypeChange();
        } else {
            this.planTypeRadios.forEach((radio) => (radio.checked = false));
            this.subPlanRadios.forEach((radio) => (radio.checked = false));
            if (this.isLineItem) {
                this.clearAllPlans();
            }
        }
    }

    handlePlanTypeChange() {
        const subPlanContainers = this.querySelectorAll('.service-plan-sub-options');
        this.subPlanRadios.forEach((radio) => (radio.checked = false));

        const selectedContainer = this.planTypeRadios[0].checked
            ? subPlanContainers[0]
            : subPlanContainers[1];

        const firstOption = selectedContainer.querySelector('.service-plan-sub-option:first-child');
        if (firstOption) {
            firstOption.querySelector('input[name*="_service_plan"]').checked = true;
            this.handleSubPlanChange();
        }
    }

    async handleSubPlanChange() {
        const selectedRadio = this.querySelector('input[name*="_service_plan"]:checked');
        if (!selectedRadio || !this.isLineItem) return;

        const ajaxCart =
            document.querySelector('cart-items') || document.querySelector('ajax-cart');
        if (!ajaxCart) return;

        ajaxCart.loading = true;
        this.loadingSpinner.classList.remove('hidden');

        try {
            // Parse the existing properties string into an object
            const properties = JSON.parse(this.properties.innerHTML);

            // Update the _service_plan value
            properties._service_plan = selectedRadio.value;

            await this.fetchCartUpdate({
                line: this.parentIndex,
                quantity: this.parentQty || 1,
                properties: properties
            });
            ajaxCart.onCartUpdate();
        } catch (error) {
            console.error('Error updating service plan:', error);
            this.showErrorToUser('Failed to update service plan. Please try again.');
        } finally {
            this.loadingSpinner.classList.add('hidden');
            ajaxCart.loading = false;
        }
    }

    showErrorToUser(message) {
        console.error(message);
        // TODO: Implement user-friendly error display
    }
}

customElements.define('service-plan', ServicePlan);
